import {
  APIError,
  encodeURLString,
  ErrorHandling,
  EVENT_EDITOR_DATA,
  EVENT_REQUEST_EDITOR_DATA,
  executeWithRetry,
  FatalErrors,
  FEATURE_SWITCHES_TO_CHECK,
  FeatureSwitchManager,
  fetchWrapper,
  findIsland,
  iFrameLauncherEvent,
  type iFrameLauncherMessage,
  type IslandDetails,
  LauncherEventType,
  Logger,
  mocksResponse,
  type RenderedClassForStudent,
  type RenderedClassForTeacher,
  type SessionEntityType,
  StudentManager,
  TeacherManager,
  UserManager,
  type UserSession,
} from "@classdojo/monsterverse-utils";
import { DEFAULT_LOCALE, initializeTranslations, isRTLLocale, translate } from "@monsterverse/i18n";

import { postToApp } from "./MessageHandler";

const DEFAULT_COUNTRY_CODE = "zz"; //Not a valid ISO code, stands for unknown
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;

type TicketResponse = {
  ticket: string;
  version: string;
  groupId: string;
  user: UserSession;
  classes?: RenderedClassForStudent[] | RenderedClassForTeacher[];
  islandName: string;
  locale: string;
  countryCode?: string;
  featureSwitches: Record<string, string>;
  customConfig?: unknown;
  featureSwitchesCustomConfig?: unknown;
  region: string;
  desiredRegion: string;
  classYear: string;
  monsterConfig?: {
    body: string;
    color: string;
    eyes: string;
    mouth: string;
    feet: string;
    version: string;
  };
  auth: "normal" | "guest_anon";
};

function getEnvUrl(base: string) {
  const awsEnv = window._AWS_ENV || "local";
  if (awsEnv === "local") {
    throw new Error("Invalid AWS env");
  } else if (awsEnv === "prod") {
    return base;
  }
  return `${base}-${awsEnv}`;
}

export async function getTicket(
  groupId: string,
  entityType: SessionEntityType,
  body: string,
  devNameAndVersionParam: string,
  editorParam: string,
  guestLinkTokenParam: string
) {
  return executeWithRetry(async () => {
    const fetchResult = await fetchWrapper(
      "ticket",
      `https://${getEnvUrl(
        "ticket-ex"
      )}.multiplayer.classdojo.com/?groupId=${groupId}&entityType=${entityType}&sessionId=${Logger.getSessionId()}${devNameAndVersionParam}${editorParam}${guestLinkTokenParam}`,
      {
        method: "POST",
        credentials: "include",
        body,
      }
    );

    if (!fetchResult.ok) {
      try {
        const json = await fetchResult.json();
        if (json && json.method && json.url && json.status && json.statusText) {
          Logger.logWarn("API Error", {
            method: json.method,
            url: json.url,
            status: json.status,
            statusText: json.statusText,
          });

          if (fetchResult.status === HTTP_UNAUTHORIZED) {
            await setupTranslations();
            ErrorHandling.instance?.triggerFatalError(FatalErrors.UserLoggedOut);
            throw new APIError(`Error in callApi: ${json.status}`, json.method, json, json.statusText);
          } else if (fetchResult.status === HTTP_FORBIDDEN) {
            await setupTranslations();
            ErrorHandling.instance?.triggerFatalError(FatalErrors.UserLoggedOut);
            throw new APIError(
              `Error in callApi: ${json.status}`,
              json.method,
              json,
              `lockedReason=[${json.lockedReason || "unknown"}]`
            );
          } else {
            throw new APIError(`Error in callApi: ${json.status}`, json.method, json, json.statusText);
          }
        }
      } catch (e) {
        if (APIError.instanceof(e)) {
          throw e;
        }
      }
      // fallback to throw a generic error if request is not ok
      throw new Error("Failed to get ticket");
    }
    return (await fetchResult.json()) as TicketResponse;
  }, 2);
}

function isEditorBuild() {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const editor = urlSearchParams.get("editor");
  return (window._AWS_ENV === "dev" || window._AWS_ENV === "local") && editor;
}

function getNameAndVersionFromURL() {
  const env = window._ENV || "local";
  if (env === "local" && !window._NGROK_HOST) {
    throw new Error("Local environment should not be checking the url subpath");
  }

  const match = new RegExp(/\/.*\/(.*-.*-.*-.*-.*)\//g).exec(window.location.pathname);
  if (match && match.length > 1) {
    return match[1];
  }
  throw new Error(`Unable to find version in url ${window.location.pathname}`);
}

function getDevNameAndVersion() {
  let version = getNameAndVersionFromURL();
  if (isEditorBuild()) {
    version = version.replace("dev", "editor");
  }
  return version;
}

async function getEditorOverrides() {
  return await new Promise<{ featureSwitches: Record<string, string> }>((resolve) => {
    const gotEditorAssets = (data: { featureSwitches: Record<string, string> }) => {
      window.removeEventListener("message", onGetEditorAssetsIFrameMessage.bind(undefined, gotEditorAssets));
      resolve(data);
    };
    window.addEventListener("message", onGetEditorAssetsIFrameMessage.bind(undefined, gotEditorAssets));
    console.log("sending request for assets");
    postToApp({ eventName: EVENT_REQUEST_EDITOR_DATA });
  });
}

function onGetEditorAssetsIFrameMessage(
  resolve: (data: { featureSwitches: Record<string, string> }) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ev: MessageEvent<any>
) {
  if (ev.data.eventName !== EVENT_EDITOR_DATA) {
    return;
  }

  resolve(ev.data.eventValues[0]);
}

let lastTicket: TicketResponse | undefined;
// eslint-disable-next-line sonarjs/cognitive-complexity
export async function getAWSHostURLs(groupID: string, entityType: SessionEntityType, bundleId?: string) {
  let wsUrl: URL;
  let hubUrl: string;
  let island: IslandDetails;
  let countryCode: string;
  let serverRegion: string;
  let desiredServerRegion: string;
  let editor = false;
  let auth: "normal" | "guest_anon" = "normal";
  let monsterConfig:
    | {
        body: string;
        color: string;
        eyes: string;
        mouth: string;
        feet: string;
        version: string;
      }
    | undefined;

  const env = window._ENV || "local";
  if (env === "local" && !window._NGROK_HOST) {
    await UserManager.instance.syncUser(entityType, () => Promise.reject());

    const localIP = window._LOCALIP || "localhost";
    const localGroupId = groupID === "default" ? "class-abcdefg" : groupID;
    wsUrl = new URL(`wss://${localIP}:8080`);
    wsUrl.searchParams.set(
      "ticket",
      encodeURLString(
        JSON.stringify({
          region: "local",
          host: "host",
          port: 1234,
          groupId: localGroupId,
          time: 0,
          entityId: UserManager.instance.getEntityId(),
          entityType: entityType === "student" ? "studentUser" : entityType,
          auth: "normal",
        })
      )
    );
    wsUrl.searchParams.set("loggerSessionId", Logger.getSessionId());

    const classId = localGroupId.startsWith("class-") ? localGroupId : undefined;
    const communityId = localGroupId.startsWith("community-") ? localGroupId : undefined;
    await FeatureSwitchManager.fetchFeatureSwitches(classId, communityId);

    //If editor build, set overrides
    if (isEditorBuild()) {
      editor = true;
      const overrides = await getEditorOverrides();
      FeatureSwitchManager.featureSwitches = { ...FeatureSwitchManager.featureSwitches, ...overrides.featureSwitches };
    }

    //Setup community members
    //The first one is real (using our mock data) and the second one is faked with different ids to test not being able to build there
    const communityMembers = [
      {
        entityId: "521fc843178a921652000007",
        entityType: "teacher",
        firstName: "Teacher",
        lastName: "One",
        avatarUrl:
          "https://pstudent.classdojo.com/monsterWorldAvatar/eyJ2ZXJzaW9uIjoiNS4xIiwiYm9keSI6Ik1vbnN0ZXJfQW50bGVyc18wMSIsImNvbG9yIjoiQ29sb3JfUmVkXzAxIiwiZXllcyI6Ik1vbnN0ZXJFeWVzX0JlYWR5XzAxX0JsYWNrIiwibW91dGgiOiJNb25zdGVyTW91dGhfV2lkZV9TcXVhcmVfMDEiLCJmZWV0IjoiTW9uc3RlclBhdHRlcm5fVkdyYWRpZW50XzAxIiwiaSI6IjQifQ==",
        plot: {
          islandType: "home",
          islandId: "fakeOne",
        },
      },
      {
        entityId: UserManager.instance.getEntityId(),
        entityType,
        firstName: UserManager.instance.getEntity()?.firstName,
        lastName: UserManager.instance.getEntity()?.lastName,
        avatarUrl: UserManager.instance.getEntity()?.avatarURL,
        plot: {
          islandType: "home",
          islandId: UserManager.instance.getEntityId(),
        },
      },
    ];

    const urlSearchParams = new URLSearchParams(window.location.search);
    const guestMode = urlSearchParams.get("guestMode") === "true";
    const inviterEntityId = guestMode
      ? // We need to mock the guest inviter entity id
        UserManager.instance.getEntityId()
      : undefined;
    if (guestMode) {
      // re-initialize UserManager to set guest user details
      await UserManager.instance.syncUser(entityType, () => Promise.reject(), true);

      auth = "guest_anon";
      monsterConfig = {
        body: "Monster_Koala_01",
        color: "Color_Red_01",
        eyes: "MonsterEyes_Duo_01_Black",
        mouth: "MonsterMouth_Med_Chip_01",
        feet: "MonsterPattern_ChestPatch_01",
        version: "5.1",
      };
    }

    wsUrl.searchParams.set(
      "ticket",
      encodeURLString(
        JSON.stringify({
          region: "local",
          host: "host",
          port: 1234,
          groupId: localGroupId,
          time: 0,
          entityId: UserManager.instance.getEntityId(),
          entityType: entityType === "student" ? "studentUser" : entityType,
          auth,
          inviterEntityId,
        })
      )
    );
    wsUrl.searchParams.set("loggerSessionId", Logger.getSessionId());

    await FeatureSwitchManager.fetchFeatureSwitches(classId, communityId);

    //If editor build, set overrides
    if (isEditorBuild()) {
      editor = true;
      const overrides = await getEditorOverrides();
      FeatureSwitchManager.featureSwitches = { ...FeatureSwitchManager.featureSwitches, ...overrides.featureSwitches };
    }

    await fetchWrapper(
      "dev",
      `https://${localIP}:8080/claimed?groupId=${localGroupId}${editor ? "&editor=true" : ""}`,
      {
        method: "POST",
        mode: "no-cors",
        body: JSON.stringify({
          featureSwitches: FeatureSwitchManager.featureSwitches,
          bundleId,
          members: communityMembers,
          featureSwitchesCustomConfig: FeatureSwitchManager.customConfig,
          classYear: "1", //mock for local build
        }),
      }
    );

    hubUrl = `https://${localIP}:8788`;

    island = {
      id: localGroupId,
      ownerName: "Local's",
      ownerAvatarURL: "",
      islandName: `Local Island`,
    };
    countryCode = "us";
    serverRegion = "us-east-1";
    desiredServerRegion = "us-east-1";
  } else {
    const awsEnv = window._AWS_ENV || "local";

    const ticketBody = {
      ...FEATURE_SWITCHES_TO_CHECK,
      bundleId,
      featureSwitches: undefined as Record<string, string> | undefined,
    };

    //If editor build, set overrides
    if (isEditorBuild()) {
      editor = true;
      const overrides = await getEditorOverrides();
      ticketBody.featureSwitches = overrides.featureSwitches;
    }

    let guestLinkToken = "";
    const urlSearchParams = new URLSearchParams(window.location.search);
    const guestLinkTokenParam = urlSearchParams.get("token");
    if (guestLinkTokenParam) {
      guestLinkToken = `&guestLinkToken=${guestLinkTokenParam}`;
    }

    //Get the ticket
    lastTicket = await getTicket(
      groupID,
      entityType,
      JSON.stringify(ticketBody),
      awsEnv === "dev" ? `&devNameAndVersion=${getDevNameAndVersion()}` : "",
      editor ? "&editor=true" : "",
      guestLinkToken
    );

    //Setup the hub iframe url
    let hubVersion = lastTicket.version;
    if (isEditorBuild()) {
      hubVersion = hubVersion.replace("editor", "dev");
    }
    hubUrl = `https://clients.multiplayer.classdojo.com/hub/${hubVersion}/index.html`;

    //Setup the websocket url
    wsUrl = new URL(`wss://${getEnvUrl("ws")}.multiplayer.classdojo.com`);
    wsUrl.searchParams.set("ticket", lastTicket.ticket);
    wsUrl.searchParams.set("loggerSessionId", Logger.getSessionId());
    wsUrl.searchParams.set("version", hubVersion);

    //Locate island from returned user data
    UserManager.instance.user = lastTicket.user;
    if (lastTicket.classes) {
      if (entityType === "teacher") {
        TeacherManager.instance.classes = lastTicket.classes as RenderedClassForTeacher[];
      } else {
        StudentManager.instance.classes = lastTicket.classes as RenderedClassForStudent[];
      }
    }
    FeatureSwitchManager.featureSwitches = lastTicket.featureSwitches;

    FeatureSwitchManager.customConfig = lastTicket.featureSwitchesCustomConfig;
    Logger.setEntityId(UserManager.instance.getEntityId());
    Logger.setClassYear(lastTicket.classYear);

    const islandDetails = findIsland(lastTicket.groupId, entityType);
    if (!islandDetails) {
      throw new Error(`[getAWSHostURLs] Unable to find island details for groupId=[${lastTicket.groupId}]`);
    }
    island = { ...islandDetails, islandName: lastTicket.islandName };
    countryCode = lastTicket.countryCode ?? DEFAULT_COUNTRY_CODE;
    serverRegion = lastTicket.region;
    desiredServerRegion = lastTicket.desiredRegion;
    auth = lastTicket.auth;
    monsterConfig = lastTicket.monsterConfig;
  }

  await setupTranslations();

  // Now that translations are setup, we can localize the name of the guest user
  if (auth === "guest_anon") {
    const user = UserManager.instance.user;
    if (user && user.type === "student") {
      user.student.firstName = translate("common.guest.name", { name: user.student.firstName });
    }
  }

  //Datadog expects United Kingdom to be under "gb" instead of "uk"
  if (countryCode === "uk") countryCode = "gb";

  return {
    wsUrl,
    hubUrl,
    island,
    countryCode,
    serverRegion,
    desiredServerRegion,
    editor,
    auth,
    monsterConfig,
  };
}

export async function setupTranslations() {
  await initializeTranslations(lastTicket?.locale || DEFAULT_LOCALE, {
    isLocal: mocksResponse(),
    build: window.build,
    logger: (level, message) => {
      switch (level) {
        case "error":
          console.error(message);
          Logger.logError(message);
          break;
        case "warn":
          console.warn(message);
          Logger.logWarn(message);
          break;
        case "info":
          console.log(message);
          Logger.logInfo(message);
          break;
      }
    },
  });

  if (isRTLLocale()) {
    // add special marker class to document body element so HTML elements
    // with class `supportsRtl` can opt-in to get rendered with `direction: rtl`
    window.document.body.classList.add("rtl");
  }
}

let iframeEl: HTMLIFrameElement | undefined;
export function handleUserDataRequests(iframe: HTMLIFrameElement) {
  iframeEl = iframe;
  window.addEventListener("message", onIFrameMessage);
}

function onIFrameMessage(ev: MessageEvent<iFrameLauncherMessage>) {
  if (ev?.data?.iFrameLauncherEvent !== iFrameLauncherEvent) {
    return;
  }

  switch (ev.data.type) {
    case LauncherEventType.GetFeatureSwitches: {
      if (FeatureSwitchManager.featureSwitches === undefined) {
        Logger.logError(
          `[STARTUP] Launcher returning undefined feature switches. lastTicket ${JSON.stringify(lastTicket)}`
        );
      }
      postMessage(LauncherEventType.GetFeatureSwitches, {
        featureSwitches: FeatureSwitchManager.featureSwitches,
        customConfig: FeatureSwitchManager.customConfig,
      });
      break;
    }
    case LauncherEventType.GetUser: {
      const env = window._ENV || "local";
      if (env === "local") {
        console.log("!! LOCAL USER", UserManager.instance.user);
        postMessage(LauncherEventType.GetUser, { user: UserManager.instance.user });
      }
      break;
    }
  }

  if (lastTicket === undefined) {
    return;
  }

  switch (ev.data.type) {
    case LauncherEventType.GetUser:
      postMessage(LauncherEventType.GetUser, { user: lastTicket.user });
      break;
    case LauncherEventType.GetClasses:
      postMessage(LauncherEventType.GetClasses, { classes: lastTicket.classes });
      break;
    case LauncherEventType.GetLocale:
      postMessage(LauncherEventType.GetLocale, { locale: lastTicket.locale || DEFAULT_LOCALE });
      break;
  }
}

function postMessage(type: LauncherEventType, data: Record<string, unknown>) {
  iframeEl?.contentWindow?.postMessage({ iFrameLauncherEvent, type, data }, "*");
}
